import { trackEvent } from "./analytics";

// Width: side-docked DevTools. Scrollbar is ~15px; bookmarks bar is 0 width.
const DOCK_WIDTH_THRESHOLD_PX = 160;
// Height: tabs + omnibox already ~90–110px. Bookmarks bar adds ~28–36px and
// used to trip a 120px threshold. Bottom-docked DevTools is typically 200px+.
const DOCK_HEIGHT_THRESHOLD_PX = 280;
const DEBUGGER_PAUSE_MS = 80;
const POLL_MS = 600;

const isGuardEnabled = (): boolean =>
  import.meta.env.VITE_DEVTOOLS_GUARD !== "false";

const isBypassed = (): boolean =>
  new URLSearchParams(window.location.search).has("peek");

const isPhoneOrTablet = (): boolean => {
  const ua = navigator.userAgent;
  if (
    /iPhone|iPod|Android.+Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  ) {
    return true;
  }

  const iPadOs =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (iPadOs || /iPad|Android/i.test(ua)) {
    return true;
  }

  return window.matchMedia("(pointer: coarse)").matches;
};

const isDockedInspector = (): boolean => {
  // Mobile browser chrome (URL bar, toolbars) looks like a docked inspector
  // if we only compare outer vs inner viewport.
  if (isPhoneOrTablet()) {
    return false;
  }

  if (window.outerWidth < 1 || window.outerHeight < 1) {
    return false;
  }

  const widthGap = window.outerWidth - window.innerWidth;
  const heightGap = window.outerHeight - window.innerHeight;
  return (
    widthGap > DOCK_WIDTH_THRESHOLD_PX || heightGap > DOCK_HEIGHT_THRESHOLD_PX
  );
};

const isDebuggerAttached = (): boolean => {
  const started = performance.now();
  // New function each time so "Never pause here" on a source line does not stick.
  Function("debugger")();
  return performance.now() - started > DEBUGGER_PAUSE_MS;
};

const isInspectHotkey = (event: KeyboardEvent): boolean => {
  const key = event.key.toLowerCase();
  if (key === "f12") {
    return true;
  }

  const withShift = event.shiftKey && (event.ctrlKey || event.metaKey);
  if (withShift && (key === "i" || key === "j" || key === "c")) {
    return true;
  }

  return event.metaKey && event.altKey && (key === "i" || key === "j" || key === "c");
};

type DevtoolsDetection = "docked" | "debugger" | "hotkey";

const trackDevtoolsGuardTriggered = (
  detection: DevtoolsDetection,
  inspectHotkey?: string
): void => {
  trackEvent("devtools_guard_triggered", {
    detection_method: detection,
    page_path: `${window.location.pathname}${window.location.search}`,
    page_host: window.location.host,
    entry_referrer: document.referrer.slice(0, 512) || "(direct)",
    ...(inspectHotkey ? { inspect_hotkey: inspectHotkey } : {}),
  });
};

export const watchDevtools = (
  onChange: (isOpen: boolean) => void
): (() => void) => {
  if (!isGuardEnabled() || isBypassed()) {
    return () => undefined;
  }

  let wasOpen = false;
  let openedByDock = false;

  const open = (detection: DevtoolsDetection, inspectHotkey?: string) => {
    if (wasOpen) {
      return;
    }
    wasOpen = true;
    openedByDock = detection === "docked";
    trackDevtoolsGuardTriggered(detection, inspectHotkey);
    onChange(true);
  };

  const poll = () => {
    const docked = isDockedInspector();

    if (wasOpen) {
      if (openedByDock && !docked) {
        wasOpen = false;
        openedByDock = false;
        onChange(false);
      }
      return;
    }

    if (docked) {
      open("docked");
      return;
    }

    if (isDebuggerAttached()) {
      open("debugger");
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (isInspectHotkey(event)) {
      open("hotkey", event.key.toLowerCase());
    }
  };

  poll();
  const id = window.setInterval(poll, POLL_MS);
  window.addEventListener("resize", poll);
  window.addEventListener("keydown", onKeyDown, true);

  return () => {
    window.clearInterval(id);
    window.removeEventListener("resize", poll);
    window.removeEventListener("keydown", onKeyDown, true);
  };
};
